import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Certification } from 'src/app/people/models/certification';
import { PersonCertification } from 'src/app/people/models/personCertification';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { LineupErrorStateMatcher } from 'src/app/shared/services/error-state-matcher.service';
import { DateLessThanValidator } from 'src/app/shared/validators/date.validator';
import { PersonCertificationWrite } from 'src/app/people/models/personCertificationWrite';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { PersonProfileService } from 'src/app/people/profile/person-profile.service';
import { StringIgnoreCaseComparator } from '../../utils/comparators';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'certification-detail-modal',
  templateUrl: './certification-detail-modal.component.html',
  styleUrls: ['./certification-detail-modal.component.scss'],
})
export class CertificationDetailModalComponent implements OnInit, OnDestroy {
  public personId: string;
  public personCertification: PersonCertification;
  public isEditMode: boolean = false;
  public isTeamSearchMode: boolean = false;
  public certifications: Array<Certification> = new Array<Certification>();
  private personCertificationsSubscription: Subscription;
  private personDetailsSubscription: Subscription;

  certificationDetailForm: UntypedFormGroup;
  dateFormGroup: UntypedFormGroup;
  matcher = new LineupErrorStateMatcher();
  filteredCertifications: Observable<Certification[]>;

  get editModeLabel(): string {
    return this.isEditMode ? 'Edit' : 'Add';
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private profileService: PersonProfileService,
    public dialogRef: MatDialogRef<CertificationDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.personId = this.data.personId;

    this.personDetailsSubscription = this.profileService.getCertifications().subscribe((certs) => {
      if (certs) {
        this.certifications = certs.sort(StringIgnoreCaseComparator('name'));
        this.setupFilter();
      }
    });

    if (this.data.personCertification) {
      this.personCertification = this.data.personCertification;
      this.isEditMode = true;
    } else {
      this.personCertification = new PersonCertification();
    }
    this.buildForm();
  }

  private setupFilter(): void {
    this.filteredCertifications = this.certificationDetailForm?.controls['name'].valueChanges.pipe(
      startWith(''),
      map((val) => this._filter(val))
    );
  }

  private _filter(value: string): Certification[] {
    if (value) {
      const filterValue = value.trim().toLowerCase();
      return this.certifications.filter((cert) =>
        cert.name.trim().toLowerCase().includes(filterValue)
      );
    }
    return this.certifications;
  }

  private buildForm(): void {
    this.dateFormGroup = this.formBuilder.group({
      startDate: this.personCertification?.id
        ? this.personCertification?.activeRange?.startDate
        : null,
      endDate: this.personCertification?.id ? this.personCertification?.activeRange?.endDate : null,
    });

    this.dateFormGroup.setValidators([DateLessThanValidator('startDate', 'endDate')]);

    this.certificationDetailForm = this.formBuilder.group({
      name: [this.personCertification ? this.personCertification.name : '', Validators.required],
      number: this.personCertification ? this.personCertification.number : '',
      dateGroup: this.dateFormGroup,
      url: this.personCertification ? this.personCertification.url : '',
    });
  }

  public save() {
    if (!this.certificationDetailForm.valid) {
      return;
    }

    this.personCertificationsSubscription = this.profileService
      .savePersonCertification(this.personId, this.personCertification.id, this.formToModel())
      .subscribe((cert) => {
        if (cert.id) {
          this.profileService.getPersonCertifications(this.personId);
          this.close();
        }
      });
  }

  private formToModel(): PersonCertificationWrite {
    let personCertificationWrite = new PersonCertificationWrite();
    personCertificationWrite.name = this.certificationDetailForm.value.name;
    personCertificationWrite.number = this.certificationDetailForm.value.number;
    personCertificationWrite.activeDate = this.dateFormGroup.get('startDate').value;
    personCertificationWrite.expirationDate = this.dateFormGroup.get('endDate').value;
    personCertificationWrite.url = this.certificationDetailForm.value.url;
    return personCertificationWrite;
  }

  public close() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    if (this.personCertificationsSubscription) this.personCertificationsSubscription.unsubscribe();
    if (this.personDetailsSubscription) this.personDetailsSubscription.unsubscribe();
  }
}
